import { useState, useEffect, useCallback } from "react";
import authService from "./authService";

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export function useAuthService(idleTimeout: number = DEFAULT_IDLE_TIMEOUT_MS): {
  authService: typeof authService;
  isIdle: boolean;
  resetIdleTimer: () => void;
} {
  const [isIdle, setIsIdle] = useState<boolean>(false);

  const resetIdleTimer = useCallback((): void => {
    setIsIdle(false);
    authService.resetIdleTimer();
  }, []);

  useEffect(() => {
    let idleTimer: ReturnType<typeof setTimeout>;

    const handleActivity = (): void => {
      resetIdleTimer();
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setIsIdle(true), idleTimeout);
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        handleActivity();
      }
    };

    // Initialize visibility change listener for token refresh
    authService.initVisibilityChangeListener();

    // Set up idle detection
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial idle timer
    idleTimer = setTimeout(() => setIsIdle(true), idleTimeout);

    // Clean up function
    return () => {
      authService.removeVisibilityChangeListener();
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(idleTimer);
    };
  }, [idleTimeout, resetIdleTimer]);

  return { authService, isIdle, resetIdleTimer };
}
